import {
    Button,
    Modal,
    Box,
    Typography,
    TextField,
    Divider,
    CircularProgress,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import DatePicker from "react-datepicker";
import { useState, useEffect } from "react";
import { createAppointment } from '../../services/appointmentService';
import { CreateAppointmentDto } from "../../types/Appointment";
import { getRealtorById } from "../../services/realtorService"
import { format, parse } from "date-fns";
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import dayjs from 'dayjs';
import "react-datepicker/dist/react-datepicker.css";



type AppointmentModalProps = {
    clientId: number;
    realtorBaseId: string;
    propertyId: number;
    realtorName?: string;
    clientName?: string;
};

const modalStyle = {
    position: "absolute" as const,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
        xs: "90%",
        sm: 400,
    },
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    borderRadius: "10px",
    boxShadow: 24,
    p: 3,
};

const CreateAppointmentModal = ({ clientId, realtorBaseId, propertyId, realtorName, clientName }: AppointmentModalProps) => {
    const [open, setOpen] = useState(false);
    const [date, setDate] = useState<string>("");
    const [time, setTime] = useState<string>("");
    const [notes, setNotes] = useState<string>("");
    const [loading, setLoading] = useState(false);
    const [realtorUserId, setRealtorUserId] = useState<number | null>(null);
    const today = dayjs().format('YYYY-MM-DD');
    const navigate = useNavigate();
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const resetForm = () => {
        setDate("");
        setTime("");
        setNotes("");
    };

    useEffect(() => {
        const fetchRealtorUserId = async () => {
            try {
                const realtor = await getRealtorById(realtorBaseId);
                if (realtor?.userProfileId) {
                    setRealtorUserId(realtor.userProfileId);
                }

            } catch (error) {
                console.error("Error al obtener el userId del realtor:", error);
            }
        };

        if (realtorBaseId) {
            fetchRealtorUserId();
        }
    }, [realtorBaseId]);

    function parseTimeStringToDate(timeString: string): Date | null {
        try {
            return parse(timeString, "hh:mm aa", new Date());
        } catch {
            return null;
        }
    }

    const handleConfirm = async () => {
        if (!date || !time) {
            handleClose();
            resetForm();
            Swal.fire({
                icon: "warning",
                title: "Campos incompletos",
                text: "Por favor, completa todos los campos requeridos.",
            });
            return;
        }

        if (!realtorUserId) {
            handleClose();
            resetForm();
            Swal.fire({
                icon: "error",
                title: "No se pudo obtener el agente",
                text: "Intenta nuevamente más tarde.",
            });
            return;
        }

        setLoading(true);

        try {
            const formatTimeForBackend = (timeString: string): string => {
                const dateObj = parse(timeString, "hh:mm aa", new Date());
                return format(dateObj, "HH:mm");
            };

            const dto: CreateAppointmentDto = {
                clientId,
                realtorId: realtorUserId,
                propertyId,
                appointmentDate: date,
                appointmentTime: formatTimeForBackend(time),
                status: "Pending",
                notes,
            };

            const response = await createAppointment(dto);

            if (response.success) {
                handleClose();
                resetForm();
                Swal.fire({
                    title: "Cita agendada correctamente",
                    text: "Revisa tu calendario para ver la cita agendada.",
                    icon: "success",
                    confirmButtonText: "Ir al calendario",
                }).then(() => {
                    navigate("/appointments/home"); 
                });
            } else {
                handleClose();
                resetForm();
                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: response.message || "Error al agendar la cita",
                });
            }
        } catch (err) {
            handleClose();
            resetForm();
            Swal.fire({
                icon: "error",
                title: "Error inesperado",
                text: "Intenta nuevamente más tarde.",
            });
        } finally {
            setLoading(false);
            handleClose();
            resetForm();
        }
    };


    return (
        <>
            <Box sx={{ mt: 2, mb: 2 }}>
                {/* Botón para abrir el modal */}
                <Button variant="contained" fullWidth onClick={handleOpen}
                    sx={{
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: "500",
                        borderRadius: "12px",
                        background: "linear-gradient(45deg, #4caf50, #2e7d32)",
                        boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                            background: "linear-gradient(45deg, #43a047, #2e7d32)",
                            boxShadow: "0 6px 25px rgba(76, 175, 80, 0.4)",
                            transform: "translateY(-2px)",
                        },
                    }}
                >
                    Agendar cita
                </Button>

            </Box>


            <Modal open={open} onClose={handleClose}>
                <Box sx={{ ...modalStyle, position: 'relative' }}>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 8,
                            color: 'grey.600',
                            '&:hover': {
                                color: 'black',
                            },
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h6" mb={2}>
                        ¡Hola {clientName}! Completa este formulario para agendar una cita con el agente y visitar la propiedad
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {/* Información del cliente */}
                    <TextField
                        label="Cliente"
                        type="text"
                        fullWidth
                        disabled
                        value={clientName || ""}
                        sx={{ mb: 2 }}
                    />

                    {/* Información del agente */}
                    <TextField
                        label="Agente"
                        type="text"
                        fullWidth
                        disabled
                        value={realtorName || ""}
                        sx={{ mb: 2 }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: 'column',
                            width: '100%',
                            flexWrap: 'wrap',
                            mb: 2,
                        }}
                    >
                        {/* Fecha */}
                        <TextField
                            label="Fecha de cita"
                            type="date"
                            value={date}
                            fullWidth
                            onChange={(e) => setDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: today }}
                            sx={{ flex: 1 }}
                        />

                        {/* Hora */}
                        <DatePicker
                            selected={time ? parseTimeStringToDate(time) : null}
                            onChange={(date: Date | null) => {
                                if (date) {
                                    const formatted = format(date, "hh:mm aa");
                                    setTime(formatted);
                                }
                            }}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={30}
                            timeCaption="Hora"
                            dateFormat="hh:mm aa"
                            customInput={
                                <TextField
                                    label="Hora de cita"
                                    variant="outlined"
                                    fullWidth
                                    value={time}
                                    InputLabelProps={{ shrink: true }}
                                />
                            }
                        />

                    </Box>
                    <TextField
                        label="Notas"
                        multiline
                        rows={3}
                        fullWidth
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <Button
                        variant="contained"
                        fullWidth
                        disabled={loading}
                        sx={{
                            mt: 2,
                            background: "linear-gradient(45deg, #4caf50, #2e7d32)",
                            boxShadow: "0 4px 20px rgba(76, 175, 80, 0.3)",
                        }}
                        onClick={handleConfirm}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Confirmar cita"}
                    </Button>
                </Box>
            </Modal>
        </>
    );
};
export default CreateAppointmentModal;